import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { cleanParams } from '@/utils/cleanParams';

import { apiDealFetch } from '..';
import type { TFetchError } from '../api-error.types';
import type {
  CompanyDetailDto,
  CompanyListRes,
  CompanySummaryDto,
  CompanyValidationRes,
  EntityCompanyListRes,
  EntityDashboardRes,
  EntityOfferListRes,
  GetAdminCompaniesParams,
  GetCompaniesParams,
  GetEntityCompaniesParams,
  GetEntityOffersParams,
  GetIntroducerSubmissionsParams,
  GetOffersParams,
  IntroducerCompanyDemandRes,
  IntroducerDemandListRes,
  IntroducerOfferListRes,
  MarketDepthRes,
  OfferDto,
  OfferFillHistoryRes,
  OfferListRes,
  OffersStatsRes,
  PaginationMeta,
  PriceHistoryPeriodType,
  PriceHistoryRes,
  SectorListRes,
  SubSectorListRes,
  ValidateCompanyBody,
} from './types';

export const DEAL_QUERY_KEYS = {
  // Sectors
  sectors: 'deal-sectors',
  subSectors: 'deal-subSectors',
  // Companies (public)
  companies: 'deal-companies',
  companyById: 'deal-companyById',
  companySummary: 'deal-companySummary',
  priceHistory: 'deal-priceHistory',
  marketDepth: 'deal-marketDepth',
  // Offers (public)
  companyOffers: 'deal-companyOffers',
  offerById: 'deal-offerById',
  offerFillHistory: 'deal-offerFillHistory',
  // Offer Stats
  offerStats: 'deal-offerStats',
  // Entity companies
  entityCompanies: 'deal-entityCompanies',
  entityOffers: 'deal-entityOffers',
  entityDashboard: 'deal-entityDashboard',
  // Admin companies
  adminCompanies: 'deal-adminCompanies',
  // Admin global offers
  adminOffers: 'deal-adminOffers',
  // Every provider's offers as an entity sees them (entity Supply)
  marketplaceOffers: 'deal-marketplaceOffers',
  // Introducer own submissions
  introducerOffers: 'deal-introducerOffers',
  introducerDemand: 'deal-introducerDemand',
  introducerCompanyDemand: 'deal-introducerCompanyDemand',
  // Validation
  validateCompany: 'deal-validateCompany',
};

// ==================== Sector Queries ====================

export const _getSectors = () => {
  return queryOptions({
    queryKey: [DEAL_QUERY_KEYS.sectors],
    queryFn: () => apiDealFetch<SectorListRes>('/sectors'),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getSubSectors = (sectorId?: string) => {
  return queryOptions({
    enabled: !!sectorId,
    queryKey: [DEAL_QUERY_KEYS.subSectors, sectorId],
    queryFn: () => apiDealFetch<SubSectorListRes>(`/sectors/${sectorId}/sub-sectors`),
    refetchOnMount: true,
    retry: 1,
  });
};

// ==================== Company Queries (public) ====================

export const _getCompanies = (params?: GetCompaniesParams) => {
  const safeParams = cleanParams(params);

  return queryOptions({
    queryKey: [DEAL_QUERY_KEYS.companies, ...Object.values(params ?? {})],
    queryFn: () => apiDealFetch<CompanyListRes>('/companies', { params: safeParams }),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getCompaniesInfinite = (params?: Omit<GetCompaniesParams, 'page'>) => {
  return infiniteQueryOptions({
    queryKey: [DEAL_QUERY_KEYS.companies, 'infinite', ...Object.values(params ?? {})],
    queryFn: ({ pageParam = 1 }) => {
      const safeParams = cleanParams({
        ...params,
        page: pageParam,
        limit: params?.limit ?? 20,
      });
      return apiDealFetch<CompanyListRes>('/companies', { params: safeParams });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.page < lastPage.pagination.totalPages) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getCompanyById = (companyId?: string) => {
  return queryOptions({
    enabled: !!companyId,
    queryKey: [DEAL_QUERY_KEYS.companyById, companyId],
    queryFn: () => apiDealFetch<CompanyDetailDto>(`/companies/${companyId}`),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getCompanySummary = (companyId?: string) => {
  return queryOptions({
    enabled: !!companyId,
    queryKey: [DEAL_QUERY_KEYS.companySummary, companyId],
    queryFn: () => apiDealFetch<CompanySummaryDto>(`/companies/${companyId}/summary`),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getPriceHistory = ({
  companyId,
  period,
}: {
  companyId?: string;
  period?: PriceHistoryPeriodType;
}) => {
  return queryOptions({
    enabled: !!companyId,
    queryKey: [DEAL_QUERY_KEYS.priceHistory, companyId, period],
    queryFn: () =>
      apiDealFetch<PriceHistoryRes>(`/companies/${companyId}/price-history`, {
        params: cleanParams({ period }),
      }),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getMarketDepth = ({ companyId }: { companyId?: string }) => {
  return queryOptions({
    queryKey: [DEAL_QUERY_KEYS.marketDepth, companyId],
    queryFn: () => apiDealFetch<MarketDepthRes>(`/companies/${companyId}/market-depth`),
    enabled: !!companyId,
  });
};

// ==================== Offer Queries (public) ====================

export const _getCompanyOffers = ({
  companyId,
  params,
}: {
  companyId?: string;
  params?: GetOffersParams;
}) => {
  const safeParams = cleanParams(params);

  return queryOptions({
    enabled: !!companyId,
    queryKey: [DEAL_QUERY_KEYS.companyOffers, companyId, ...Object.values(params ?? {})],
    queryFn: () =>
      apiDealFetch<OfferListRes>(`/companies/${companyId}/offers`, { params: safeParams }),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getOfferById = ({ companyId, offerId }: { companyId?: string; offerId?: string }) => {
  return queryOptions({
    enabled: !!companyId && !!offerId,
    queryKey: [DEAL_QUERY_KEYS.offerById, companyId, offerId],
    queryFn: () => apiDealFetch<OfferDto>(`/companies/${companyId}/offers/${offerId}`),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getOfferFillHistory = ({
  companyId,
  offerId,
}: {
  companyId?: string;
  offerId?: string;
}) => {
  return queryOptions({
    enabled: !!companyId && !!offerId,
    queryKey: [DEAL_QUERY_KEYS.offerFillHistory, companyId, offerId],
    queryFn: () =>
      apiDealFetch<OfferFillHistoryRes>(`/companies/${companyId}/offers/${offerId}/fill-history`),
    refetchOnMount: true,
    retry: 1,
  });
};

// ==================== Offer Stats Query ====================

export const _getOfferStats = () => {
  return queryOptions({
    queryKey: [DEAL_QUERY_KEYS.offerStats],
    queryFn: () => apiDealFetch<OffersStatsRes>('/offer-stats'),
    refetchOnMount: true,
    retry: 1,
  });
};

// ==================== Entity Company Queries ====================

export const _getEntityCompanies = (params?: GetEntityCompaniesParams) => {
  const safeParams = cleanParams(params);

  return queryOptions({
    queryKey: [DEAL_QUERY_KEYS.entityCompanies, ...Object.values(params ?? {})],
    queryFn: () => apiDealFetch<EntityCompanyListRes>('/entity/companies', { params: safeParams }),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getEntityCompaniesInfinite = (params?: Omit<GetEntityCompaniesParams, 'page'>) => {
  return infiniteQueryOptions({
    queryKey: [DEAL_QUERY_KEYS.entityCompanies, 'infinite', ...Object.values(params ?? {})],
    queryFn: ({ pageParam = 1 }) => {
      const safeParams = cleanParams({
        ...params,
        page: pageParam,
        limit: params?.limit ?? 20,
      });
      return apiDealFetch<EntityCompanyListRes>('/entity/companies', { params: safeParams });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.page < lastPage.pagination.totalPages) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getEntityOffers = (params?: GetEntityOffersParams) => {
  const safeParams = cleanParams(params);

  return queryOptions({
    queryKey: [DEAL_QUERY_KEYS.entityOffers, ...Object.values(params ?? {})],
    queryFn: () =>
      apiDealFetch<EntityOfferListRes>('/entity/companies/offers', { params: safeParams }),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getEntityDashboard = () => {
  return queryOptions({
    queryKey: [DEAL_QUERY_KEYS.entityDashboard],
    queryFn: () => apiDealFetch<EntityDashboardRes>('/entity/companies/dashboard'),
    refetchOnMount: true,
    retry: 1,
  });
};

// ==================== Company Validation ====================

export const _validateCompany = (body: ValidateCompanyBody) => {
  return queryOptions({
    queryKey: [
      DEAL_QUERY_KEYS.validateCompany,
      body.companyId,
      body.fields.name,
      body.fields.legalName,
    ],
    queryFn: () =>
      apiDealFetch<CompanyValidationRes>('/companies/validate', {
        method: 'POST',
        body,
      }),
    retry: 1,
  });
};

// ==================== Admin Company Queries ====================

export const _getAdminCompanies = (params?: GetAdminCompaniesParams) => {
  const safeParams = cleanParams(params);

  return queryOptions({
    queryKey: [DEAL_QUERY_KEYS.adminCompanies, ...Object.values(params ?? {})],
    queryFn: () => apiDealFetch<EntityCompanyListRes>('/admin/companies', { params: safeParams }),
    refetchOnMount: true,
    retry: 1,
  });
};

// ==================== Introducer Submissions Queries ====================

// A 403 means the caller is not an Active introducer; retrying cannot change that.
export const retryIntroducerSubmissions = (failureCount: number, error: Error) =>
  failureCount < 1 && (error as TFetchError | null)?.status !== 403;

const nextSubmissionsPage = ({ pagination }: { pagination: PaginationMeta }) =>
  pagination.page < pagination.totalPages ? pagination.page + 1 : undefined;

export const _getIntroducerOffersInfinite = (
  params?: Omit<GetIntroducerSubmissionsParams, 'page'>,
) =>
  infiniteQueryOptions({
    queryKey: [DEAL_QUERY_KEYS.introducerOffers, 'infinite', ...Object.values(params ?? {})],
    queryFn: ({ pageParam }) =>
      apiDealFetch<IntroducerOfferListRes>('/introducer/offers', {
        params: cleanParams({ ...params, page: pageParam }),
      }),
    initialPageParam: 1,
    getNextPageParam: nextSubmissionsPage,
    refetchOnMount: true,
    retry: retryIntroducerSubmissions,
  });

export const _getIntroducerDemandInfinite = (
  params?: Omit<GetIntroducerSubmissionsParams, 'page'>,
) =>
  infiniteQueryOptions({
    queryKey: [DEAL_QUERY_KEYS.introducerDemand, 'infinite', ...Object.values(params ?? {})],
    queryFn: ({ pageParam }) =>
      apiDealFetch<IntroducerDemandListRes>('/introducer/demand', {
        params: cleanParams({ ...params, page: pageParam }),
      }),
    initialPageParam: 1,
    getNextPageParam: nextSubmissionsPage,
    refetchOnMount: true,
    retry: retryIntroducerSubmissions,
  });

export const _getIntroducerCompanyDemand = (
  companyId: string,
  params?: GetIntroducerSubmissionsParams,
) =>
  queryOptions({
    queryKey: [DEAL_QUERY_KEYS.introducerCompanyDemand, companyId, ...Object.values(params ?? {})],
    queryFn: () =>
      apiDealFetch<IntroducerCompanyDemandRes>(`/introducer/companies/${companyId}/demand`, {
        params: cleanParams(params),
      }),
    refetchOnMount: true,
    retry: retryIntroducerSubmissions,
  });

export const _getIntroducerCompanyDemandInfinite = (
  companyId: string,
  params?: Omit<GetIntroducerSubmissionsParams, 'page'>,
) =>
  infiniteQueryOptions({
    queryKey: [
      DEAL_QUERY_KEYS.introducerCompanyDemand,
      'infinite',
      companyId,
      ...Object.values(params ?? {}),
    ],
    queryFn: ({ pageParam }) =>
      apiDealFetch<IntroducerCompanyDemandRes>(`/introducer/companies/${companyId}/demand`, {
        params: cleanParams({ ...params, page: pageParam }),
      }),
    initialPageParam: 1,
    getNextPageParam: nextSubmissionsPage,
    refetchOnMount: true,
    retry: retryIntroducerSubmissions,
  });
